import { Injectable } from '@angular/core';
import { InitializeInput, Offerings, CustomerInfo, PurchaseParams, PurchaseResult } from './types';

@Injectable({
  providedIn: 'root',
})
export class RevenueCatClient {
  protected isInitialized = false;

  protected configuredAppUserId?: string;

  /**
   * If it has already been initialized for the same user - simple return. Consider this as a normal flow.
   * A different user (same-tab logout and sign-in) re-identifies the SDK so purchases go to the right account.
   */
  async initialize({ attrs, ...configuration }: InitializeInput): Promise<void> {
    if (this.isInitialized && this.configuredAppUserId === configuration.appUserId) {
      return;
    }

    const client = await this.useClient();

    if (this.isInitialized) {
      await client.getSharedInstance().changeUser(configuration.appUserId);
    } else {
      client.configure(configuration);
    }

    this.configuredAppUserId = configuration.appUserId;
    this.isInitialized = true;

    if (attrs) {
      // Best-effort: a failed attribute sync must not leave the (usable) SDK half-initialised.
      await client
        .getSharedInstance()
        .setAttributes(attrs)
        .catch(() => undefined);
    }
  }

  async getOfferings(): Promise<Offerings> {
    const client = await this.useClient();

    return client.getSharedInstance().getOfferings();
  }

  async getCustomerInfo(): Promise<CustomerInfo> {
    const client = await this.useClient();

    return client.getSharedInstance().getCustomerInfo();
  }

  async purchase(params: PurchaseParams): Promise<PurchaseResult> {
    const client = await this.useClient();

    return client.getSharedInstance().purchase(params);
  }

  protected useClient = () => import('@revenuecat/purchases-js').then(({ Purchases }) => Purchases);
}
